import { getDoc, getDocs } from 'firebase/firestore';

const DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes

// In-memory TTL cache for Firestore reads. Keyed by caller-supplied strings
// (e.g. "schoolId:classes/{id}/students") so school-scoped data never collides.
// Module-level Map: persists across view mounts for the lifetime of the tab.
const entries = new Map();

export const dataCacheService = {
  get(key) {
    const hit = entries.get(key);
    if (!hit) return undefined;
    if (hit.expiresAt <= Date.now()) {
      entries.delete(key);
      return undefined;
    }
    return hit.data;
  },

  set(key, data, ttl = DEFAULT_TTL) {
    entries.set(key, { data, expiresAt: Date.now() + ttl });
    return data;
  },

  // Cache-or-compute: runs producer on miss, stores the result.
  async resolve(key, producer, ttl = DEFAULT_TTL) {
    const cached = this.get(key);
    if (cached !== undefined) return cached;
    return this.set(key, await producer(), ttl);
  },

  // getDocs() wrapper: returns cached [{ id, ...data }] or fetches + caches.
  async getDocs(key, ref, ttl = DEFAULT_TTL) {
    return this.resolve(key, async () => {
      const snap = await getDocs(ref);
      return snap.docs.map(d => ({ id: d.id, ...d.data() }));
    }, ttl);
  },

  // getDoc() wrapper: returns cached { id, ...data } | null.
  async getDoc(key, ref, ttl = DEFAULT_TTL) {
    return this.resolve(key, async () => {
      const snap = await getDoc(ref);
      return snap.exists() ? { id: snap.id, ...snap.data() } : null;
    }, ttl);
  },

  // Drop every entry whose key starts with prefix (e.g. a schoolId scope).
  invalidate(prefix) {
    for (const key of entries.keys()) {
      if (!prefix || key.startsWith(prefix)) entries.delete(key);
    }
  },

  // Drop entries whose path (after the "scope:" prefix) equals `path` or sits
  // under it — lets writers invalidate e.g. "classes" or "classes/{id}/students"
  // without knowing which school scope cached them.
  invalidatePath(path) {
    for (const key of entries.keys()) {
      const i = key.indexOf(':');
      const p = i >= 0 ? key.slice(i + 1) : key;
      if (p === path || p.startsWith(path + '/')) entries.delete(key);
    }
  },

  clear() {
    entries.clear();
  },
};
